import { useEffect, useState } from 'react';
import { initOutbox } from './lib/offline/outbox.js';
import { restoreQueries } from './lib/offline/persist.js';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { AuthProvider, useAuth, useAccess } from './context/AuthContext.js';
import { ToastHost } from './components/ToastHost.js';
import { UpdateBanner } from './components/UpdateBanner.js';
import { emitError } from './lib/toastBus.js';
import { LoginPage } from './pages/LoginPage.js';
import { LandingPage } from './pages/LandingPage.js';
import { PrivacyPage } from './pages/PrivacyPage.js';
import { ClaimPage } from './pages/ClaimPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { ChecksPage } from './pages/ChecksPage.js';
import { ChecklistPage } from './pages/ChecklistPage.js';
import { CheckItemPage } from './pages/CheckItemPage.js';
import { RunSummaryPage } from './pages/RunSummaryPage.js';
import { SearchPage } from './pages/SearchPage.js';
import { ProfilePage } from './pages/ProfilePage.js';
import { HelpPage } from './pages/HelpPage.js';
import { StartCheckPage } from './pages/StartCheckPage.js';
import { ReportPage } from './pages/ReportPage.js';
import { AdminLayout } from './pages/admin/AdminLayout.js';
import { AssetsPage } from './pages/admin/AssetsPage.js';
import { AssetDetailPage } from './pages/admin/AssetDetailPage.js';
import { EquipmentPage } from './pages/admin/EquipmentPage.js';
import { TeamsPage } from './pages/admin/TeamsPage.js';
import { MembersPage } from './pages/admin/MembersPage.js';
import { SchedulesPage } from './pages/admin/SchedulesPage.js';
import { ActivityPage } from './pages/admin/ActivityPage.js';
import { LocationsPage } from './pages/admin/LocationsPage.js';
import { QualificationsPage } from './pages/admin/QualificationsPage.js';
import { RosterPage } from './pages/admin/RosterPage.js';
import { ReportsPage, UnitReportPrintPage } from './pages/admin/ReportsPage.js';
import { ShiftsPage } from './pages/ShiftsPage.js';
import { CoverNewPage } from './pages/CoverNewPage.js';
import { CoverDetailPage } from './pages/CoverDetailPage.js';
import { SwapNewPage, SwapDetailPage } from './pages/SwapPages.js';
import { OpsPage } from './pages/OpsPage.js';
import { NowPage } from './pages/NowPage.js';
import { FaultsPage, FaultDetailPage, FaultNewPage } from './pages/FaultPages.js';
import { ScratchPage } from './pages/ScratchPages.js';
import { ActivityPage as ActivityDetailPage, ActivityFormPage } from './pages/ActivityPages.js';
import { MfaSetupPage } from './pages/MfaSetupPage.js';
import { UnitPickerPage } from './components/UnitSwitcher.js';
import { useMe, useSystemMe } from './lib/queries.js';
import { SystemDashboardPage, SystemOrgPage } from './pages/system/SystemPages.js';
import { NoAccessPage } from './pages/NoAccessPage.js';
import { ApiError } from './lib/api.js';
import { theme } from './theme.js';

function describeError(error: unknown): string {
  return (error instanceof Error && error.message) || 'Something went wrong';
}

// A role that needs MFA without it enrolled (e.g. just promoted to admin):
// refetch /me so MfaGate swaps in the setup screen, instead of toasting.
function handleApiError(error: unknown) {
  if (error instanceof ApiError && error.code === 'MFA_REQUIRED') {
    void qc.invalidateQueries({ queryKey: ['me'] });
    void qc.invalidateQueries({ queryKey: ['system', 'me'] });
    return;
  }
  // Not in any unit / not a system admin: the gates show a screen for it.
  if (error instanceof ApiError && (error.code === 'NO_ORG' || error.code === 'NOT_SYSTEM_ADMIN')) return;
  emitError(describeError(error));
}

const qc = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1 } },
  queryCache: new QueryCache({ onError: handleApiError }),
  mutationCache: new MutationCache({ onError: handleApiError }),
});

// Checks opened before, and changes still waiting to sync, come back from
// the phone's storage before anything renders (so it works with no signal).
const ready = Promise.all([restoreQueries(qc), initOutbox(qc)]).catch(() => {});

export function App() {
  const [restored, setRestored] = useState(false);
  useEffect(() => { void ready.then(() => setRestored(true)); }, []);
  if (!restored) return null;
  return (
    <QueryClientProvider client={qc}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <ToastHost />
        <UpdateBanner />
      </AuthProvider>
    </QueryClientProvider>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();

  // Public whether signed in or not, and outside the unit/MFA gates.
  if (pathname === '/privacy') return <PrivacyPage />;
  if (loading) return <FullScreenLoading />;

  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/claim/:token" element={<ClaimPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <OrgGate>
      <MfaGate>
        <SignedInRoutes />
      </MfaGate>
    </OrgGate>
  );
}

function SignedInRoutes() {
  const { me, isAdmin, canManage } = useAccess();

  return (
    <Routes>
      {me?.systemAdmin && <Route path="/system" element={<SystemDashboardPage />} />}
      {me?.systemAdmin && <Route path="/system/orgs/:orgId" element={<SystemOrgPage />} />}
      <Route path="/" element={<DashboardPage />} />
      <Route path="/checks" element={<ChecksPage />} />
      <Route path="/checks/new" element={<StartCheckPage />} />
      <Route path="/checks/:runId" element={<ChecklistPage />} />
      <Route path="/checks/:runId/report" element={<ReportPage />} />
      <Route path="/checks/:runId/items/:itemId" element={<CheckItemPage />} />
      <Route path="/checks/:runId/submit" element={<RunSummaryPage />} />
      <Route path="/search" element={<SearchPage />} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/shifts" element={<ShiftsPage />} />
      <Route path="/cover/new" element={<CoverNewPage />} />
      <Route path="/cover/:id" element={<CoverDetailPage />} />
      <Route path="/swap/new" element={<SwapNewPage />} />
      <Route path="/swap/:id" element={<SwapDetailPage />} />
      <Route path="/ops" element={<OpsPage />} />
      <Route path="/now" element={<NowPage />} />
      <Route path="/faults" element={<FaultsPage />} />
      <Route path="/faults/new" element={<FaultNewPage />} />
      <Route path="/faults/:id" element={<FaultDetailPage />} />
      <Route path="/scratch/:id" element={<ScratchPage />} />
      <Route path="/activity/new" element={<ActivityFormPage />} />
      <Route path="/activity/:id" element={<ActivityDetailPage />} />
      <Route path="/activity/:id/edit" element={<ActivityFormPage />} />
      <Route path="/claim/:token" element={<ClaimPage />} />

      {isAdmin && <Route path="/reports/print" element={<UnitReportPrintPage />} />}

      {/* Admin — guarded. Admins get everything; a team's leader or deputy only Teams. */}
      {isAdmin ? (
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Navigate to="/admin/assets" replace />} />
          <Route path="assets" element={<AssetsPage />} />
          <Route path="assets/:assetId" element={<AssetDetailPage />} />
          <Route path="equipment" element={<EquipmentPage />} />
          <Route path="teams" element={<TeamsPage />} />
          <Route path="roster" element={<RosterPage />} />
          <Route path="members" element={<MembersPage />} />
          <Route path="quals" element={<QualificationsPage />} />
          <Route path="schedules" element={<SchedulesPage />} />
          <Route path="locations" element={<LocationsPage />} />
          <Route path="activity" element={<ActivityPage />} />
          <Route path="reports" element={<ReportsPage />} />
        </Route>
      ) : canManage ? (
        <Route path="/admin" element={<AdminLayout />}>
          <Route path="teams" element={<TeamsPage />} />
          <Route path="*" element={<Navigate to="/admin/teams" replace />} />
          <Route index element={<Navigate to="/admin/teams" replace />} />
        </Route>
      ) : (
        <Route path="/admin/*" element={<Navigate to="/" replace />} />
      )}

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Settles which unit is in use before anything else loads. Someone in
 * several units picks one (once — it's remembered); someone in one unit, or
 * whose remembered unit they've since left, uses the one GET /me answered for.
 */
function OrgGate({ children }: { children: React.ReactNode }) {
  const { data: me, isLoading, error } = useMe();
  const { orgId, switchOrg } = useAuth();
  const known = !!me && me.orgs.some((o) => o.id === orgId);
  const adopt = !!me && !known && me.orgs.length < 2;

  useEffect(() => { if (adopt) switchOrg(me!.orgId); }, [adopt, me, switchOrg]);

  if (isLoading) return <FullScreenLoading />;
  if (error instanceof ApiError && error.code === 'NO_ORG') return <SystemOnly />;
  if (!me) return <>{children}</>;
  if (!known) return me.orgs.length > 1 ? <UnitPickerPage /> : <FullScreenLoading />;
  return <>{children}</>;
}

/**
 * Signed in but in no unit: a system admin gets just the System pages;
 * anyone else is told they have no access.
 */
function SystemOnly() {
  const { data: sys, isLoading } = useSystemMe();
  if (isLoading) return <FullScreenLoading />;
  if (!sys) return <NoAccessPage />;
  if (sys.mfaSetupRequired) return <MfaSetupPage />;
  return (
    <Routes>
      <Route path="/system" element={<SystemDashboardPage standalone />} />
      <Route path="/system/orgs/:orgId" element={<SystemOrgPage />} />
      <Route path="*" element={<Navigate to="/system" replace />} />
    </Routes>
  );
}

/** Blocks the app for a role that requires MFA until it's set up. */
function MfaGate({ children }: { children: React.ReactNode }) {
  const { data: me, isLoading } = useMe();
  if (isLoading) return <FullScreenLoading />;
  if (me?.mfaSetupRequired) return <MfaSetupPage />;
  return <>{children}</>;
}

function FullScreenLoading() {
  return (
    <div style={{
      height: '100dvh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', color: theme.textMuted, fontFamily: theme.font,
    }}>
      Loading…
    </div>
  );
}
